import {
  useQueryClient,
  useQuery,
  useMutation,
  keepPreviousData,
} from "@tanstack/react-query";
import qs from "qs";
import {
  downloadProductionChartApi,
  downloadProductionChartByCategoryIdApi,
  editTemplates,
  getFutureChartList,
  getProductionChart,
  getProductionChartList,
  getProductionChartTemplateList,
  productionChartBatchSplitApi,
} from "../api/useProductionApi";
import { format } from "date-fns";
import { getCategoryLists, getRecipeLists } from "../../Templates/api";
import { errorMessage, printFile, successMessage } from "../../../utils/helper";

const productionChartKeys = {
  all: ["productionChart"],
  lists: () => [...productionChartKeys.all, "productionChartlist"],
  chartlist: (filters) => [...productionChartKeys.lists(), filters],
  futureChart: (filters) => [
    ...productionChartKeys.lists(),
    filters,
    "futureList",
  ],
  detail: (id) => [...productionChartKeys.lists(), id],
  templates: (id) => [...productionChartKeys.lists(), id, "tempalteList"],
  category: (id) => [...productionChartKeys.lists(), id, "categoryList"],
  recipe: (id) => [...productionChartKeys.lists(), id, "recipeList"],
  stores: (id) => [...productionChartKeys.lists(), id, "storesList"],
};

export const useProductionChartQuery = ({
  list,
  futureList,
  detId,
  pagination,
  debouncedText,
  sorting,
  storeId,
  dates,
  isRecipe,
  setProductionchart,
  productionchart,
  selectedIndex,
  isCategoryBasedOtherRecipes = 0,
}) => {
  const queryClient = useQueryClient();
  let params = {};
  let storeIds = storeId.map((v) => v.id);
  if (debouncedText) {
    params = { ...params, search: debouncedText };
  }

  if (storeIds.length > 0) {
    params = { ...params, store_id: storeIds };
  }
  if (dates.length > 0) {
    params = {
      ...params,
      start_date: format(dates[0].startDate, "yyyy-MM-dd"),
      end_date: format(dates[0].endDate, "yyyy-MM-dd"),
    };
  }
  if (futureList && storeIds.length > 0) {
    params = { ...params, stores: storeIds };
  }

  if (sorting?.length > 0) {
    const sortByParams = sorting.reduce(
      (obj, item) =>
        Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
      {}
    );
    params = {
      ...params,
      sort: sortByParams,
    };
  }

  const query = {
    params,
    paramsSerializer: (params) => qs.stringify(params, { encode: true }),
  };

  //list query
  const listQuery = useQuery({
    queryKey: productionChartKeys.chartlist({ query, pagination }),
    queryFn: () => getProductionChartList({ query, pagination }),
    placeholderData: keepPreviousData,
    enabled: list,
  });

  const futureListQuery = useQuery({
    queryKey: productionChartKeys.futureChart({ query, pagination }),
    queryFn: () => getFutureChartList({ payload: params, pagination }),
    placeholderData: keepPreviousData,
    enabled: futureList,
  });

  const productionChartDetails = useQuery({
    queryKey: productionChartKeys.detail(detId),
    queryFn: () =>
      getProductionChart({
        date: detId,
        isRecipe,
        category_list: isCategoryBasedOtherRecipes,
      }),
    enabled: !!detId || (isRecipe === 0 && isRecipe === 1),
    refetchOnWindowFocus: false,
  });

  const templateList = useQuery({
    queryKey: productionChartKeys.templates(detId),
    queryFn: () => getProductionChartTemplateList(detId),
    // enabled: !!detId,
  });
  const categorylistQuery = useQuery({
    queryKey: productionChartKeys.category(),
    queryFn: () => getCategoryLists(),
    placeholderData: keepPreviousData,
    enabled: true,
  });
  const recipelistQuery = useQuery({
    queryKey: productionChartKeys.recipe(isRecipe),
    queryFn: () => getRecipeLists("finishedproduct"),
    enabled: (isRecipe === 0 && true) || (isRecipe === 1 && true),
  });

  const editTemplate = useMutation({
    mutationFn: ({ _, payload }) => editTemplates(payload),
    onError: (error) => {
      errorMessage(error.response.data.message);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: productionChartKeys.lists(),
      });
      if (data.success) {
        successMessage(data.message);
        setProductionchart((draft) => {
          draft.isBowlcapaciytyExceed = false;
        });
        return variables.handleModal();
      } else {
        setProductionchart((draft) => {
          draft.isBowlcapaciytyExceed = true;
          draft.templateBatchDetails = data?.data?.batches;
          draft.batchTemplateDetails = data?.data;
          draft.temp_index = 0;
        });
      }
    },
  });

  const downloadProductionChart = useMutation({
    mutationFn: ({ _, payload }) => downloadProductionChartApi(payload),
    onError: (error) => {
      errorMessage(error.response.data.message);
    },
    onSuccess: (res, variables) => {
      printFile(res.data);
    },
  });
  //batch split api integrations

  const updateProductionchartBatchSplit = useMutation({
    mutationFn: ({ payload }) => productionChartBatchSplitApi(payload),
    onError: (error, variables) => {
      const errorData = error?.response?.data?.data;

      // Only show error if success is false and errorData is an array
      if (Array.isArray(errorData)) {
        setProductionchart((draft) => {
          // draft.batchDetails = errorData;
          // Clear all previous errors first
          draft?.batchDetails?.[selectedIndex]?.products?.forEach((product) => {
            product.batches?.forEach((batch) => {
              batch.hasError = false;
            });
          });
          // Set error only for batches in errorData
          errorData.forEach((err) => {
            const product = draft?.batchDetails?.[
              selectedIndex
            ]?.products?.find((p) => p.product_id === err.product_id);
            if (
              product &&
              Array.isArray(product.batches) &&
              typeof err.batch === "number"
            ) {
              // API batch is 1-based, JS array is 0-based
              const batchIdx = err.batch - 1;
              if (product.batches[batchIdx]) {
                product.batches[batchIdx].hasError = true;
              }
            }
          });
        });
      }
      variables?.handleDeleteBatchDialog(false);
    },

    onSuccess: (data, variables) => {
      // If success, clear all errors
      if (data.success) {
        console.log("data?.data?.batches", data?.data?.batches);

        setProductionchart((draft) => {
          draft?.batchDetails?.[selectedIndex]?.products?.forEach((product) => {
            product.batches?.forEach((batch) => {
              batch.hasError = false;
            });
          });
        });
      }
      setProductionchart((draft) => {
        draft.templateBatchDetails = data?.data?.batches;
        draft.isBatchSplit = false;
        const product =
          draft?.batchDetails?.[selectedIndex]?.products?.[
            productionchart.productIndex
          ];
        if (product?.batches?.[productionchart.batchInex]) {
          product.batches[productionchart.batchIndex].hasError = data.success
            ? false
            : true;
        }
      });
      queryClient.invalidateQueries({
        queryKey: productionChartKeys.productionChartlist(),
      });
      successMessage(data?.message);
      variables.handleDeleteBatchDialog(false);
    },
  });

  // Download PC by category
  const downloadProductionChartByCategory = useMutation({
    mutationFn: ({ _, payload }) =>
      downloadProductionChartByCategoryIdApi(payload),
    onError: (error) => {
      errorMessage(error.response.data.message);
    },
    onSuccess: (res, variables) => {
      printFile(res.data);
    },
  });

  return {
    queryClient,
    productionChartKeys,
    listQuery,
    productionChartDetails,
    futureListQuery,
    templateList,
    categorylistQuery,
    recipelistQuery,
    editTemplate,
    downloadProductionChart,
    updateProductionchartBatchSplit,
    downloadProductionChartByCategory,
  };
};
